import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MapPin,
  Sparkles,
  TrendingUp,
  X,
} from "lucide-react";

export const Route = createFileRoute("/realizacje")({
  head: () => ({
    meta: [
      { title: "Realizacje — SkuteczneStrony" },
      {
        name: "description",
        content:
          "Portfolio wdrożonych stron internetowych i sklepów e-commerce. Realne case study: branża, czas realizacji, wynik PageSpeed, efekt biznesowy.",
      },
      { property: "og:title", content: "Realizacje — SkuteczneStrony" },
      {
        property: "og:description",
        content:
          "Wybrane wdrożenia SkuteczneStrony: strony B2B, sklepy, lendingi. Cyfry, nie obietnice.",
      },
    ],
    links: [
      { rel: "preconnect", href: "https://images.unsplash.com", crossOrigin: "anonymous" },
    ],
  }),
  component: RealizacjePage,
});

type Category =
  | "Wszystkie"
  | "B2B"
  | "E-commerce"
  | "Lokalny biznes"
  | "Landing"
  | "Portfolio";

type Project = {
  slug: string;
  title: string;
  client: string;
  category: Exclude<Category, "Wszystkie">;
  location: string;
  date: string;
  img: string;
  cover: string;
  excerpt: string;
  metrics: { label: string; value: string }[];
  tags: string[];
  url?: string;
  accent: string;
  span?: "tall" | "wide";
  featured?: boolean;
};

const PROJECTS: Project[] = [
  {
    slug: "elektroprofi",
    title: "ELEKTROPROFI — instalacje przemysłowe",
    client: "ELEKTROPROFI Sp. z o.o.",
    category: "B2B",
    location: "Katowice",
    date: "06.2026",
    img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Kompletna witryna B2B z portfolio realizacji, kwalifikacjami SEP oraz interaktywną galerią case study. Cyber-minimalistyczny design.",
    metrics: [
      { label: "PageSpeed mobile", value: "98" },
      { label: "Czas realizacji", value: "9 dni" },
      { label: "Zapytań / mies.", value: "+340%" },
    ],
    tags: ["Next-gen", "Portfolio", "CCTV", "Smart Home"],
    url: "/demo/elektryk",
    accent: "#00F0FF",
    span: "wide",
    featured: true,
  },
  {
    slug: "bloom-shop",
    title: "Bloom & Co. — sklep z roślinami",
    client: "Bloom & Co.",
    category: "E-commerce",
    location: "Warszawa",
    date: "05.2026",
    img: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Sklep e-commerce z płatnościami online, integracją kurierów i automatyzacją powiadomień. Konwersja na poziomie 4,8%.",
    metrics: [
      { label: "Konwersja", value: "4.8%" },
      { label: "Średni koszyk", value: "+27%" },
      { label: "Czas wdrożenia", value: "3 tyg." },
    ],
    tags: ["Stripe", "InPost", "Newsletter"],
    accent: "#00F5A0",
    span: "tall",
    featured: true,
  },
  {
    slug: "horizon-estate",
    title: "Horizon Estate — deweloper premium",
    client: "Horizon Estate",
    category: "B2B",
    location: "Gdańsk",
    date: "04.2026",
    img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Prezentacja inwestycji deweloperskiej z interaktywnym wyborem mieszkań, kalkulatorem rat i wirtualnym spacerem 3D.",
    metrics: [
      { label: "Leady miesięcznie", value: "+180%" },
      { label: "PageSpeed desktop", value: "100" },
      { label: "Czas wdrożenia", value: "4 tyg." },
    ],
    tags: ["3D tour", "Kalkulator", "CRM"],
    accent: "#6366F1",
    featured: true,
  },
  {
    slug: "nordic-law",
    title: "Nordic Law — kancelaria prawna",
    client: "Nordic Law Group",
    category: "B2B",
    location: "Kraków",
    date: "03.2026",
    img: "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Stonowana wizytówka B2B z systemem rezerwacji konsultacji i bazą wiedzy. Skupienie na konwersji formularza.",
    metrics: [
      { label: "Form. konsultacji", value: "+220%" },
      { label: "Bounce rate", value: "−41%" },
      { label: "Czas wdrożenia", value: "10 dni" },
    ],
    tags: ["Booking", "Blog", "SEO"],
    accent: "#A78BFA",
  },
  {
    slug: "atelier-22",
    title: "Atelier 22 — studio fotografii",
    client: "Atelier 22",
    category: "Portfolio",
    location: "Poznań",
    date: "02.2026",
    img: "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Portfolio fotograficzne z lightboxem, ochroną zdjęć przed kopiowaniem i panelem klienta do podglądu sesji.",
    metrics: [
      { label: "LCP", value: "1.1s" },
      { label: "Sesje miesięcznie", value: "+95%" },
      { label: "Czas wdrożenia", value: "12 dni" },
    ],
    tags: ["Lightbox", "Galeria", "Panel klienta"],
    accent: "#F472B6",
    span: "wide",
  },
  {
    slug: "forge-fitness",
    title: "Forge Fitness — klub treningowy",
    client: "Forge Fitness",
    category: "Lokalny biznes",
    location: "Wrocław",
    date: "01.2026",
    img: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Strona klubu fitness z grafikiem zajęć, zapisami online i panelem trenerów. Pełna integracja z systemem karnetów.",
    metrics: [
      { label: "Zapisy online", value: "+310%" },
      { label: "Karnety / mies.", value: "+62%" },
      { label: "Czas wdrożenia", value: "2 tyg." },
    ],
    tags: ["Booking", "Karnety", "Trenerzy"],
    accent: "#F97316",
  },
  {
    slug: "lumen-launch",
    title: "Lumen — landing produktowy",
    client: "Lumen Labs",
    category: "Landing",
    location: "Remote",
    date: "12.2025",
    img: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Wysoko-konwertujący lending pre-orderowy z animacjami scroll, integracją Stripe i listą oczekujących.",
    metrics: [
      { label: "Conv. rate", value: "11.4%" },
      { label: "Pre-orderów", value: "1 240" },
      { label: "Czas wdrożenia", value: "6 dni" },
    ],
    tags: ["Stripe", "Waitlist", "Motion"],
    accent: "#22D3EE",
  },
  {
    slug: "north-coffee",
    title: "North Coffee — palarnia",
    client: "North Coffee Roasters",
    category: "E-commerce",
    location: "Gdynia",
    date: "11.2025",
    img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=720&q=70",
    cover:
      "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1600&q=80",
    excerpt:
      "Sklep z kawą speciality, subskrypcje miesięczne, integracja z magazynem i Allegro Smart.",
    metrics: [
      { label: "Subskrypcje", value: "420" },
      { label: "Retencja 3m", value: "78%" },
      { label: "Czas wdrożenia", value: "3 tyg." },
    ],
    tags: ["Subskrypcje", "Allegro", "Magazyn"],
    accent: "#FACC15",
  },
];

const CATEGORIES: Category[] = [
  "Wszystkie",
  "B2B",
  "E-commerce",
  "Lokalny biznes",
  "Landing",
  "Portfolio",
];

function RealizacjePage() {
  const router = useRouter();
  const [cat, setCat] = useState<Category>("Wszystkie");
  const [open, setOpen] = useState<number | null>(null);

  const filtered = useMemo(
    () => (cat === "Wszystkie" ? PROJECTS : PROJECTS.filter((p) => p.category === cat)),
    [cat],
  );

  const close = useCallback(() => setOpen(null), []);
  const next = useCallback(
    () => setOpen((i) => (i === null ? null : (i + 1) % filtered.length)),
    [filtered.length],
  );
  const prev = useCallback(
    () => setOpen((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length)),
    [filtered.length],
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, next, prev]);

  useEffect(() => {
    setOpen(null);
  }, [cat]);

  const handleBack = useCallback(() => {
    // If we have history, browser back restores TanStack scroll restoration position.
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({ to: "/", hash: "realizacje" });
    }
  }, [router]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#030508] text-white antialiased">
      {/* Ambient glows */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div
          className="absolute -left-40 top-0 h-[520px] w-[520px] rounded-full opacity-[0.18]"
          style={{
            background: "radial-gradient(circle, #00F5A0 0%, transparent 65%)",
          }}
        />
        <div
          className="absolute -right-40 top-1/3 h-[520px] w-[520px] rounded-full opacity-[0.18]"
          style={{
            background: "radial-gradient(circle, #6366F1 0%, transparent 65%)",
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
          }}
        />
      </div>

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#030508]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 md:px-10">
          <button
            type="button"
            onClick={handleBack}
            className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300 transition hover:border-white/25 hover:text-white"
            aria-label="Wróć"
          >
            <ArrowLeft
              size={12}
              className="transition-transform duration-300 group-hover:-translate-x-0.5"
            />
            Wróć
          </button>
          <Link
            to="/"
            className="bg-clip-text text-[15px] font-semibold tracking-tight text-transparent"
            style={{
              backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
            }}
          >
            SkuteczneStrony
          </Link>
          <Link
            to="/"
            hash="cta"
            className="hidden rounded-full bg-white px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-black transition hover:scale-[1.03] sm:inline-flex"
          >
            Darmowe demo
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-5 pt-16 md:px-10 md:pt-24">
        <div className="mx-auto max-w-7xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-medium uppercase tracking-[0.28em] text-slate-300 backdrop-blur-xl">
            <Sparkles size={12} className="text-[#00F5A0]" />
            Portfolio wdrożeń
          </div>
          <h1 className="mt-6 max-w-5xl text-balance text-4xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl md:text-7xl">
            Realne wdrożenia.{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
              }}
            >
              Realne liczby.
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
            {PROJECTS.length} wybranych projektów — od wizytówek B2B przez sklepy
            e-commerce po lendingi pre-orderowe. Kliknij kafelek, by zobaczyć
            metryki, zakres prac i wynik.
          </p>

          {/* KPI strip */}
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Wdrożeń", v: "120+" },
              { k: "Śr. PageSpeed", v: "97" },
              { k: "Śr. czas wdrożenia", v: "11 dni" },
              { k: "Klient. retencja", v: "92%" },
            ].map((s) => (
              <div
                key={s.k}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 backdrop-blur-xl"
              >
                <div
                  className="bg-clip-text text-2xl font-black tracking-tight text-transparent"
                  style={{
                    backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
                  }}
                >
                  {s.v}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {s.k}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="relative px-5 py-14 md:px-10 md:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="-mx-5 overflow-x-auto px-5 md:mx-0 md:px-0">
            <div className="inline-flex min-w-full gap-1 rounded-full border border-white/[0.08] bg-white/[0.02] p-1 backdrop-blur-xl md:min-w-0">
              {CATEGORIES.map((c) => {
                const active = c === cat;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCat(c)}
                    className={`relative whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition md:px-5 md:py-2.5 ${
                      active ? "text-black" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {active && (
                      <span
                        className="absolute inset-0 -z-10 rounded-full"
                        style={{
                          backgroundImage:
                            "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
                          boxShadow: "0 0 28px rgba(0,245,160,0.35)",
                        }}
                      />
                    )}
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-slate-500">
            {filtered.length}{" "}
            {filtered.length === 1 ? "realizacja" : "realizacji"}
          </div>

          {/* Grid */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:[grid-auto-rows:280px] lg:[grid-auto-rows:320px]">
            {filtered.map((p, i) => {
              const span =
                p.span === "tall"
                  ? "md:row-span-2"
                  : p.span === "wide"
                    ? "md:col-span-2"
                    : "";
              return (
                <motion.button
                  key={p.slug}
                  type="button"
                  onClick={() => setOpen(i)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.2) }}
                  whileHover={{ y: -4 }}
                  className={`group relative overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0A0D14] text-left transition-colors hover:border-white/20 ${span}`}
                  style={{ minHeight: 260 }}
                >
                  <img
                    src={p.img}
                    alt={p.title}
                    loading={i < 3 ? "eager" : "lazy"}
                    decoding="async"
                    width={720}
                    height={540}
                    className="absolute inset-0 h-full w-full object-cover opacity-70 transition-[transform,opacity] duration-700 group-hover:scale-[1.06] group-hover:opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
                  <div
                    className="pointer-events-none absolute inset-0 opacity-0 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      background: `radial-gradient(420px 280px at 70% 20%, ${p.accent}55, transparent 70%)`,
                    }}
                  />

                  <div className="absolute left-5 right-5 top-5 flex items-start justify-between gap-3">
                    <span
                      className="rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white/85 backdrop-blur-md"
                    >
                      {p.category}
                    </span>
                    <span
                      className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-white/10 text-white opacity-0 backdrop-blur-md transition group-hover:opacity-100"
                      style={{ boxShadow: `0 0 22px ${p.accent}55` }}
                    >
                      <ArrowUpRight size={14} />
                    </span>
                  </div>

                  <div className="absolute inset-x-5 bottom-5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-300/80">
                      <MapPin size={10} />
                      {p.location}
                      <span className="opacity-40">·</span>
                      <Calendar size={10} />
                      {p.date}
                    </div>
                    <h3 className="mt-2 text-lg font-bold leading-snug tracking-tight text-white md:text-xl">
                      {p.title}
                    </h3>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      {p.metrics.slice(0, 2).map((m) => (
                        <div
                          key={m.label}
                          className="rounded-lg border border-white/10 bg-black/40 px-2.5 py-1.5 backdrop-blur-md"
                        >
                          <div
                            className="text-sm font-black tracking-tight"
                            style={{ color: p.accent }}
                          >
                            {m.value}
                          </div>
                          <div className="text-[9px] uppercase tracking-[0.16em] text-slate-400">
                            {m.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-20 overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.02] to-transparent p-8 backdrop-blur-2xl md:p-12">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.28em] text-slate-400">
                  <TrendingUp size={12} className="text-[#00F5A0]" />
                  Twoja kolej
                </div>
                <h2 className="mt-3 text-3xl font-black tracking-tight text-white md:text-4xl">
                  Następna realizacja może być Twoja.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400 md:text-base">
                  Darmowe demo Twojej nowej strony w 72 godziny. Płacisz dopiero,
                  gdy projekt Ci się spodoba.
                </p>
              </div>
              <Link
                to="/"
                hash="cta"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00F5A0] to-[#6366F1] px-7 py-3.5 text-sm font-black uppercase tracking-[0.16em] text-black shadow-lg shadow-[#00F5A0]/25 transition hover:brightness-110"
              >
                Zamów darmowe demo
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox / detail */}
      <AnimatePresence>
        {open !== null && filtered[open] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 px-4 py-6"
            onClick={close}
          >
            <button
              onClick={close}
              className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-black/40 text-white/70 transition hover:text-white"
              aria-label="Zamknij"
            >
              <X size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-3 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-black/40 text-white/70 transition hover:border-white/40 hover:text-white md:left-8"
              aria-label="Poprzednie"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-3 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-black/40 text-white/70 transition hover:border-white/40 hover:text-white md:right-8"
              aria-label="Następne"
            >
              <ChevronRight size={20} />
            </button>

            <motion.div
              key={open}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={(e) => e.stopPropagation()}
              className="grid w-full max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-[#070A12] md:grid-cols-[1.4fr_1fr]"
              style={{
                boxShadow: `0 0 80px ${filtered[open].accent}26`,
              }}
            >
              <div className="relative aspect-square w-full bg-black md:aspect-auto md:min-h-[520px]">
                <img
                  src={filtered[open].cover}
                  alt={filtered[open].title}
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/60 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/85 backdrop-blur-md">
                  {filtered[open].category}
                </span>
              </div>
              <div className="flex flex-col p-7 md:p-10">
                <div
                  className="text-[10px] font-mono uppercase tracking-[0.22em]"
                  style={{ color: filtered[open].accent }}
                >
                  Realizacja {String(open + 1).padStart(2, "0")} /{" "}
                  {String(filtered.length).padStart(2, "0")}
                </div>
                <h3 className="mt-4 text-2xl font-black leading-tight tracking-tight text-white md:text-3xl">
                  {filtered[open].title}
                </h3>
                <div className="mt-2 text-sm text-slate-400">
                  {filtered[open].client}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-slate-300/85">
                  {filtered[open].excerpt}
                </p>

                <div className="mt-6 grid grid-cols-3 gap-2">
                  {filtered[open].metrics.map((m) => (
                    <div
                      key={m.label}
                      className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3"
                    >
                      <div
                        className="text-lg font-black tracking-tight"
                        style={{ color: filtered[open].accent }}
                      >
                        {m.value}
                      </div>
                      <div className="mt-1 text-[9px] uppercase tracking-[0.16em] text-slate-500">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {filtered[open].tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-medium text-slate-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-auto grid grid-cols-2 gap-4 border-t border-white/[0.06] pt-7">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Lokalizacja
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-sm text-white">
                      <MapPin
                        size={13}
                        style={{ color: filtered[open].accent }}
                      />
                      {filtered[open].location}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      Wdrożenie
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-sm text-white">
                      <Calendar
                        size={13}
                        style={{ color: filtered[open].accent }}
                      />
                      {filtered[open].date}
                    </div>
                  </div>
                </div>

                {filtered[open].url && (
                  <Link
                    to={filtered[open].url!}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-black transition hover:scale-[1.02]"
                  >
                    Otwórz na żywo
                    <ExternalLink size={13} />
                  </Link>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
